import type React from 'react';
import { faComments, faSimCard } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MeasuringPointStatus, type SensorQuotaType } from '@/graphql/types';
import type { ProjectMeasuringPointRowViewData } from '../../utils/projectMeasuringPointRowViewData';
import classNames from '../../../../../utils/classNames';
import { MEASURING_POINT_CELL_CLASSES, TOOLTIP_TRIGGER_CLASSES } from './cellClasses';
import { UsageDetailItem } from './UsageDetailItem';
import { DEVICE_STATUS_DOT_CLASSES, StatusDot } from '../StatusDot/StatusDot';
import { getUsageSeverityClasses, UsageProgressBar } from '../UsageProgressBar/UsageProgressBar';
import Tooltip from '../../../../../utils/tooltip';

const statusIndicatorClassByStatus: Record<MeasuringPointStatus, string> = {
    [MeasuringPointStatus.Online]: DEVICE_STATUS_DOT_CLASSES.online,
    [MeasuringPointStatus.Offline]: DEVICE_STATUS_DOT_CLASSES.offline,
    [MeasuringPointStatus.Inactive]: DEVICE_STATUS_DOT_CLASSES.inactive,
    [MeasuringPointStatus.OutOfData]: 'bg-error',
    [MeasuringPointStatus.UpdateReady]: DEVICE_STATUS_DOT_CLASSES.online,
    [MeasuringPointStatus.DownloadingUpdate]: DEVICE_STATUS_DOT_CLASSES.online,
};

const statusLabelByStatus: Record<MeasuringPointStatus, () => string> = {
    [MeasuringPointStatus.Online]: () => gettext('ONLINE'),
    [MeasuringPointStatus.Offline]: () => gettext('OFFLINE'),
    [MeasuringPointStatus.Inactive]: () => gettext('INACTIVE'),
    [MeasuringPointStatus.OutOfData]: () => gettext('OVERVIEW_SENSOR_STATUS_OUT_OF_DATA'),
    [MeasuringPointStatus.UpdateReady]: () => gettext('OVERVIEW_SENSOR_STATUS_UPDATE_READY'),
    [MeasuringPointStatus.DownloadingUpdate]: () =>
        gettext('OVERVIEW_SENSOR_STATUS_DOWNLOADING_UPDATE'),
};

// Below the warning range the bar keeps its default colour.
function getUsageFillClassName({ isWarning, isLimit }: SensorQuotaType): string | undefined {
    return isWarning || isLimit ? getUsageSeverityClasses(isLimit).bar : undefined;
}

function QuotaWarningIcon({
    icon,
    isLimit,
    label,
}: {
    icon: IconDefinition;
    isLimit: boolean;
    label: string;
}): React.ReactElement {
    return (
        <span
            className={classNames(
                'inline-flex items-center',
                getUsageSeverityClasses(isLimit).text
            )}
            role="img"
            aria-label={label}
        >
            <FontAwesomeIcon icon={icon} aria-hidden />
        </span>
    );
}

interface ProjectMeasuringPointStatusCellProps {
    viewData: ProjectMeasuringPointRowViewData;
    onRestart: () => void;
}

export function ProjectMeasuringPointStatusCell({
    viewData,
    onRestart,
}: ProjectMeasuringPointStatusCellProps): React.ReactElement {
    const {
        dataQuotaWarning,
        dataUsage,
        hasDataQuotaInfo,
        hasSmsQuotaInfo,
        hasStatusDetails,
        renderedStatusSecondaryLabel,
        shouldRenderRestartAction,
        smsQuotaWarning,
        smsUsage,
        status,
    } = viewData;
    const statusLabel = statusLabelByStatus[status]();

    const statusSummary = (
        <div
            className={classNames(
                'flex items-center justify-between gap-1',
                hasStatusDetails && TOOLTIP_TRIGGER_CLASSES
            )}
            {...(hasStatusDetails ? { tabIndex: 0 } : {})}
        >
            <div className="inline-flex items-center gap-1">
                <StatusDot colorClassName={statusIndicatorClassByStatus[status]} size="small" />
                <span className={renderedStatusSecondaryLabel ? 'leading-tight' : undefined}>
                    <span>{statusLabel}</span>
                    {renderedStatusSecondaryLabel && (
                        <>
                            <br />
                            {shouldRenderRestartAction ? (
                                <button
                                    type="button"
                                    className="cursor-pointer text-left underline"
                                    onClick={onRestart}
                                >
                                    {renderedStatusSecondaryLabel}
                                </button>
                            ) : (
                                <span>{renderedStatusSecondaryLabel}</span>
                            )}
                        </>
                    )}
                </span>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
                {dataQuotaWarning && (
                    <QuotaWarningIcon
                        icon={faSimCard}
                        isLimit={dataQuotaWarning.isLimit}
                        label={gettext('DATA_WARNINGS')}
                    />
                )}
                {smsQuotaWarning && (
                    <QuotaWarningIcon
                        icon={faComments}
                        isLimit={smsQuotaWarning.isLimit}
                        label={gettext('USAGE_WARNING')}
                    />
                )}
            </div>
        </div>
    );

    if (!hasStatusDetails) {
        return <td className={MEASURING_POINT_CELL_CLASSES}>{statusSummary}</td>;
    }

    const statusDetails = (
        <div
            className="flex min-w-56 flex-col gap-2"
            role="dialog"
            aria-label={gettext('STATUS_AND_USAGE_DETAILS')}
        >
            {hasDataQuotaInfo && (
                <UsageDetailItem icon={faSimCard}>
                    {dataUsage?.percent ? (
                        <>
                            <strong>
                                {gettext('MEASURING_POINT_DATA_USAGE_THIS_MONTH')}{' '}
                                {dataUsage.percent.toFixed(1)}%
                            </strong>
                            <UsageProgressBar
                                percent={dataUsage.percent}
                                fillClassName={getUsageFillClassName(dataUsage)}
                                ariaLabel={interpolate(
                                    gettext('DATA_USAGE_PERCENT'),
                                    { pct: dataUsage.percent.toFixed(1) },
                                    true
                                )}
                            />
                        </>
                    ) : (
                        <>
                            <strong>{gettext('MEASURING_POINT_DATA_USAGE_THIS_MONTH')}</strong>
                            <strong>{gettext('UNKNOWN')}</strong>
                        </>
                    )}
                </UsageDetailItem>
            )}
            {hasSmsQuotaInfo && (
                <UsageDetailItem icon={faComments}>
                    {smsUsage ? (
                        <>
                            <strong>
                                {gettext('SMS_USAGE_THIS_MONTH')}: {smsUsage.percent.toFixed(1)}%
                            </strong>
                            <UsageProgressBar
                                percent={smsUsage.percent}
                                fillClassName={getUsageFillClassName(smsUsage)}
                                ariaLabel={interpolate(
                                    gettext('SMS_USAGE_PERCENT'),
                                    { pct: smsUsage.percent.toFixed(1) },
                                    true
                                )}
                            />
                        </>
                    ) : (
                        <>
                            <strong>{gettext('SMS_USAGE_THIS_MONTH')}: 0 / ∞</strong>
                            <UsageProgressBar percent={0} ariaLabel={gettext('SMS_UNLIMITED')} />
                        </>
                    )}
                </UsageDetailItem>
            )}
        </div>
    );

    return (
        <td className={MEASURING_POINT_CELL_CLASSES}>
            <Tooltip
                rounded
                interactive
                // By default an interactive popover goes inside the table cell,
                // where it opens far away from its status.
                appendTo={() => document.body}
                content={statusDetails}
                placement="bottom-start"
                maxWidth={288}
            >
                {statusSummary}
            </Tooltip>
        </td>
    );
}
